import { IAuthCommon, IAuthTable } from './auth.interfaces';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ActivatedRoute, Router } from '@angular/router';
import { tap } from 'rxjs/internal/operators/tap';
import swal from 'sweetalert2';
import {
  map,
  exhaustMap,
  catchError,
  withLatestFrom,
  concatMap,
  filter,
  switchMap,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { Store, select } from '@ngrx/store';
import * as moment from 'moment';
import { selectDefaultAuthEntity } from './auth.selectors';
import { AuthService } from './../services/auth.service';

import {
  Login,
  Logout,
  AuthActionTypes,
  LoginSuccessRedirect,
  LoginSuccess,
  LoginFailure,
  RefreshTokenFailure,
  RefreshToken,
  RefreshTokenSuccess,
  CheckLoginFailure,
  CheckLogin,
  SignUp,
  SignUpSuccess,
  SignUpFailure,
  DisplayError,
  ResetPasswordComplete,
  ResetPassword,
  ForgotPasswordComplete,
  ForgotPassword,
  ForgotPasswordError,
  RemoteLogin,
} from './auth.actions';
import {
  LoadResidentProfile,
  RemoveAllProfiles,
} from './../../core/store/resident-profile/resident-profile.actions';
import { getStrataPlanNumberFriendlyName } from './../../core/store/ui/ui.selectors';
import {
  SetLoginCheckComplete,
  SetIsLoggedInFalse,
  SetIsLoggedInTrue,
  FetchLotsAndUnitsWithStrataPlan,
  UIActionTypes,
  LoadActionStatus,
  LoadActionStatusSuccess,
} from './../../core/store/ui/ui.actions';
import { environment } from '../../../environments/environment';
import { IStore } from 'src/app/core/root.store';
import { RemoveAllLots } from 'src/app/core/store/lot/lot.actions';
import { RemoveAllEmergencyContacts } from 'src/app/core/store/emergency-contact/emergency-contact.actions';
import { RemoveAllPets } from 'src/app/core/store/pet/pet.actions';
import { RemoveAllVehicles } from 'src/app/core/store/vehicle/vehicle.actions';
import { UserLogin } from 'src/app/shared/services/roles.service';
import { ApproverLoginSuccess } from './approver/approver-auth.actions';
import { SearcherLoginSuccess } from './searcher/searcher-auth.actions';
import { LocationStrategy } from '@angular/common';

@Injectable()
export class AuthEffects {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType<Login>(AuthActionTypes.LOGIN),
      switchMap((action) => {
        return this.authService.login(action.payload).pipe(
          map((authResponse: IAuthCommon) => {
            localStorage.setItem(
              environment.auth.token,
              authResponse.accessToken
            );
            localStorage.setItem(
              environment.auth.token_expiry,
              authResponse.expiresIn.toString()
            );
            localStorage.setItem(
              environment.auth.refresh_token,
              authResponse.refreshToken
            );

            let authState: IAuthTable = {
              entities: {
                '5334f7c7-16e6-4714-9fce-8a2a6e60e79c': {
                  accessToken: authResponse.accessToken,
                  expiresIn: authResponse.expiresIn,
                  refreshToken: authResponse.refreshToken,
                  loggedIn: true,
                },
              },
              ids: ['5334f7c7-16e6-4714-9fce-8a2a6e60e79c'],
            };
            localStorage.setItem(environment.auth.user_login, UserLogin.Admin);
            return new LoginSuccess(authState, action.redirectTo);
          }),
          catchError((error) => {
            return of(new LoginFailure(error));
          })
        );
      })
    )
  );

  lremoteLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RemoteLogin>(AuthActionTypes.REMOTE_LOGIN),
      switchMap((action) => {
        return this.authService.remoteLogin(action.payload.toString()).pipe(
          map((authResponse: IAuthCommon) => {
            localStorage.setItem(
              environment.auth.token,
              authResponse.accessToken
            );
            localStorage.setItem(
              environment.auth.token_expiry,
              authResponse.expiresIn.toString()
            );
            localStorage.setItem(
              environment.auth.refresh_token,
              authResponse.refreshToken
            );

            let authState: IAuthTable = {
              entities: {
                '5334f7c7-16e6-4714-9fce-8a2a6e60e79c': {
                  accessToken: authResponse.accessToken,
                  expiresIn: authResponse.expiresIn,
                  refreshToken: authResponse.refreshToken,
                  loggedIn: true,
                },
              },
              ids: ['5334f7c7-16e6-4714-9fce-8a2a6e60e79c'],
            };
            localStorage.setItem(environment.auth.user_login, UserLogin.Admin);
            return new LoginSuccess(authState);
          }),
          catchError((error) => {
            return of(new LoginFailure(error));
          })
        );
      })
    )
  );

  signUp$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SignUp>(AuthActionTypes.SIGN_UP),
      switchMap((action) => {
        return this.authService.signUp(action.payload).pipe(
          map((res) => new SignUpSuccess(res)),
          catchError((err) => of(new SignUpFailure(err)))
        );
      })
    )
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ResetPassword>(AuthActionTypes.RESET_PASSWORD),
      switchMap((action) => {
        return this.authService.resetPassword(action.payload).pipe(
          map(() => new ResetPasswordComplete()),
          catchError((err) => of(new DisplayError(err))) // TODO: create its own error effect to not show random errors.
        );
      })
    )
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ForgotPassword>(AuthActionTypes.FORGOT_PASSWORD),
      switchMap((action) => {
        return this.authService.forgotPassword(action.payload).pipe(
          map(() => new ForgotPasswordComplete()),
          catchError((err) => of(new ForgotPasswordError(err)))
        );
      })
    )
  );

  signUpSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<SignUpSuccess>(AuthActionTypes.SIGN_UP_SUCCESS),
        tap((res) => console.log(res))
      ),
    { dispatch: false }
  );

  signUpFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<SignUpFailure>(AuthActionTypes.SIGN_UP_FAILURE),
        tap((res) => console.log(res))
      ),
    { dispatch: false }
  );

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RefreshToken>(AuthActionTypes.REFRESH_TOKEN),
      withLatestFrom(
        this.store$.select(
          selectDefaultAuthEntity('5334f7c7-16e6-4714-9fce-8a2a6e60e79c')
        )
      ),
      switchMap(([, authUser]) => {
        if (!authUser) {
          return of(new Logout());
        }

        return this.authService.refreshToken(authUser.refreshToken).pipe(
          map((authResponse: IAuthCommon) => {
            localStorage.setItem(
              environment.auth.token,
              authResponse.accessToken
            );
            localStorage.setItem(
              environment.auth.token_expiry,
              authResponse.expiresIn.toString()
            );
            localStorage.setItem(
              environment.auth.refresh_token,
              authResponse.refreshToken
            );

            let authState: IAuthTable = {
              entities: {
                '5334f7c7-16e6-4714-9fce-8a2a6e60e79c': {
                  accessToken: authResponse.accessToken,
                  expiresIn: authResponse.expiresIn,
                  refreshToken: authResponse.refreshToken,
                  loggedIn: true,
                },
              },
              ids: ['5334f7c7-16e6-4714-9fce-8a2a6e60e79c'],
            };
            return new RefreshTokenSuccess(authState);
          }),
          catchError((error) => {
            return of(new RefreshTokenFailure(error));
          })
        );
      })
    )
  );

  checkLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckLogin>(AuthActionTypes.CHECK_LOGIN),
      map((action) => {
        let accessToken = localStorage.getItem(environment.auth.token);
        if (accessToken) {
          let expiresIn = +localStorage.getItem(environment.auth.token_expiry);
          let refreshToken = localStorage.getItem(
            environment.auth.refresh_token
          );
          let authState: IAuthTable = {
            entities: {
              '5334f7c7-16e6-4714-9fce-8a2a6e60e79c': {
                accessToken,
                expiresIn,
                refreshToken,
                loggedIn: true,
              },
            },
            ids: ['5334f7c7-16e6-4714-9fce-8a2a6e60e79c'],
          };
          const currentUtcUnixTime = moment().utc().unix();
          if (expiresIn > currentUtcUnixTime) {
            if (
              localStorage.getItem(environment.auth.user_login) ===
              UserLogin.Approver
            ) {
              return new ApproverLoginSuccess(authState);
            }
            if (
              localStorage.getItem(environment.auth.user_login) ===
              UserLogin.Searcher
            ) {
              return new SearcherLoginSuccess(authState);
            } else {
              return new LoginSuccess(authState);
            }
          } else {
            return new CheckLoginFailure();
          }
        } else {
          return new SetLoginCheckComplete();
        }
      })
    )
  );

  checkLoginFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CheckLoginFailure>(AuthActionTypes.CHECK_LOGIN_FAILURE),
      concatMap(() => [
        new SetLoginCheckComplete(),
        new SetIsLoggedInFalse(),
        new Logout(),
      ])
    )
  );

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoginSuccess>(AuthActionTypes.LOGIN_SUCCESS),
      withLatestFrom(
        this.store$.select(
          selectDefaultAuthEntity('5334f7c7-16e6-4714-9fce-8a2a6e60e79c')
        )
      ),
      switchMap(([action, authUser]) => {
        return of(authUser).pipe(
          concatMap(() => [
            new SetLoginCheckComplete(),
            new SetIsLoggedInTrue(),
            new LoadResidentProfile(),
            new LoginSuccessRedirect(action.redirectTo),
            new FetchLotsAndUnitsWithStrataPlan(),
          ])
        );
      })
    )
  );

  loadActionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadActionStatus>(UIActionTypes.LOAD_ACTION_STATUS),
      switchMap((action) => this.authService.getActionStatus(action.payload)),
      map((actionStatus: any) => {
        return actionStatus;
      }),
      concatMap((actionStatus: any) => [
        new LoadActionStatusSuccess(actionStatus),
      ])
    )
  );

  loginRedirect$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<LoginSuccessRedirect>(AuthActionTypes.LOGIN_SUCCESS_REDIRECT),
        tap((payload) => {
          if (
            localStorage.getItem(environment.auth.user_login) ===
            UserLogin.Admin
          ) {
            let pdfPath = this.locationStrategy.path().split('/');
            if (!!payload.redirectTo) {
              this.router.navigateByUrl(payload.redirectTo);
            } else if (pdfPath[1] === UserLogin.ViewPdf) {
              this.router.navigate(pdfPath);
            } else this.router.navigate([environment.auth.auth_success_url]);
          } else if (
            localStorage.getItem(environment.auth.user_login) ===
            UserLogin.Approver
          ) {
            this.router.navigate([environment.auth.auth_success_url_approver]);
          }
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType<Logout>(AuthActionTypes.LOGOUT),
      withLatestFrom(this.store$.select(getStrataPlanNumberFriendlyName)),
      map(([action, friendlyName]) => {
        localStorage.removeItem(environment.auth.token);
        localStorage.removeItem(environment.auth.token_expiry);
        localStorage.removeItem(environment.auth.refresh_token);
        localStorage.removeItem(environment.auth.user_login);
        return new SetIsLoggedInFalse();
      })
    )
  );

  loginError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<LoginFailure>(AuthActionTypes.LOGIN_FAILURE),
        tap((err: any) => {})
      ),
    { dispatch: false }
  );

  displayError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<DisplayError>(AuthActionTypes.DISPLAY_ERROR),
        tap((action) => {
          swal.fire({
            position: 'center',
            icon: 'info',
            title: action.payload,
            showConfirmButton: true,
            timer: 5000,
          });
        })
      ),
    { dispatch: false }
  );

  refreshTokenError$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RefreshTokenFailure>(AuthActionTypes.REFRESH_TOKEN_FAILURE),
      concatMap((err: any) => [
        new DisplayError('Your session has timed out. Please login again.'),
        new Logout(),
        new RemoveAllProfiles(),
        new RemoveAllLots(),
        new RemoveAllEmergencyContacts(),
        new RemoveAllPets(),
        new RemoveAllVehicles(),
      ])
    )
  );

  constructor(
    private actions$: Actions,
    private router: Router,
    private authService: AuthService,
    private store$: Store<IStore>,
    private locationStrategy: LocationStrategy
  ) {}
}
